/**
 * Feature flags (build-time, Vite env).
 *
 * Small, explicit gate helpers. Each reads an `import.meta.env.VITE_*` string and
 * treats ONLY the literal `'true'` as on — an unset / any-other value is OFF. This
 * keeps a not-yet-fully-wired surface invisible in prod without shipping a dead
 * view, while remaining trivially testable.
 */

/**
 * walkable-dao-001-9 — the member-facing "property status → proposal graduation"
 * view. Gated OFF by default: the member-facing READ of a member's own graduating
 * registry property depends on a backend owner-scoped registry read edge that is
 * not yet built (dao-property-registry has no `ic_principal` owner column and is
 * Otto/CSV-fed; the member read edge + registry owner-keying are the flagged
 * dependency, entangled with the PENDING triage-ownership disposition). The
 * frontend ships built-to-contract and gated; flip this env to `true` once the
 * backend read edge lands.
 */
export function isPropertyGraduationViewEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_PROPERTY_GRADUATION_VIEW === 'true';
}

/**
 * onb-001-3-fe — the Step-1 lost-II recovery touchpoints (recovery-setup
 * acknowledgment gate, 2nd-factor CTA, persistent nudge, clean-conflict phase).
 *
 * Ships DARK. Gated OFF by default so the recovery UI is invisible until the live
 * flip — which stays gated on outside-counsel sign-off + all-three ratification +
 * Menley's 2026-06-23 crypto-erasure conditions (epic-onb-001). Flag OFF ⇒ today's
 * onboarding behavior EXACTLY: no recovery-setup step, no nudge, the successful
 * II-link proceeds straight to Step-2 as before.
 *
 * NOTE: this is the SUITE-LOCAL recovery-touchpoints flag. It is distinct from the
 * auth-package `ONBOARDING_GATE_ENABLED` onboarding-gate flag, which was RETIRED in
 * auth 2.0.0 (the II-link gate is now unconditional — see ProfileWizardPage). This
 * flag gates only the recovery touchpoints added by onb-001-3-fe, never the gate.
 */
export function isOnboardingGateEnabled(): boolean {
  return import.meta.env.VITE_ONBOARDING_GATE_ENABLED === 'true';
}

/**
 * ae-001-4 — the ADR-043 member submission capture wizard (`/submit`), the
 * describe-first universal front of the submission-&-attestation envelope.
 *
 * Gated OFF by default (scope-stop feature-flag pattern, AI-P4-04): the wizard
 * renders + collects + emits the frozen ae-001-3 `ArtifactCollection` entirely
 * client-side, but the submit-POST has NO live envelope-ingest endpoint yet — it
 * rides the per-domain assessor service, which is not built (ADR-043 §6; the
 * Dragnet assessor is Ottertor-name-reconciliation gated). So staging/prod keep
 * the flag OFF (nav item hidden, `/submit` route unregistered) until the ingest
 * endpoint lands; flip `VITE_ENABLE_SUBMISSION_CAPTURE=true` then. The final
 * submit action NEVER fabricates a success/attested result while the flag is on
 * for dev — it surfaces a reserved/pending-wiring state (failure-as-plausible-
 * value rule 8/9).
 */
export function isSubmissionCaptureEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_SUBMISSION_CAPTURE === 'true';
}

/**
 * ae-001-6 — the ADR-043 member credential-status surface (`/credentials`): a
 * SELF-ONLY read of the live attestation-record canister (ae-001-2) showing the
 * current verification status of the member's own credential, its append-only
 * lineage, and a proactive notice when the status downgrades (NFR6).
 *
 * Gated OFF by default (scope-stop feature-flag pattern, AI-P4-04 — same as the
 * ae-001-4/-5 sibling surfaces). The surface is fully wired and fail-closed: it
 * reads the live canister and every member legitimately reads "not verified"
 * until an authorized assessor writes the first attestation (the assessor is
 * Ottertor-name/counsel-gated, out of scope). We keep the nav item hidden and
 * the `/credentials` route unregistered in staging/prod until the alpha design
 * lights it up; flip `VITE_ENABLE_CREDENTIAL_STATUS=true` then. Flag OFF ⇒ the
 * nav + routes are byte-identical to before.
 */
export function isCredentialStatusEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_CREDENTIAL_STATUS === 'true';
}

/**
 * ae-001-7 — the ADR-043 member flag surface: a "report a problem" control on a
 * submission/attestation + the member's own flag list. Reasonless, accept-never-
 * reject, no existence oracle, owner-scoped (backend ae-001-9-2, live on
 * staging). Gated OFF by default (scope-stop pattern, AI-P4-04 — same as the
 * ae-001-4/-5/-6 sibling surfaces): the control/route are not rendered until the
 * alpha design places the control on a concrete surface and a Menley voice-pass
 * lands; flip `VITE_ENABLE_FLAG_REPORTING=true` then. Flag OFF ⇒ byte-identical.
 */
export function isFlagReportingEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_FLAG_REPORTING === 'true';
}
